import { useCallback, useEffect, useRef, useState } from 'react'
import * as jose from 'jose'
import { ChronoUnit, Instant, LocalDateTime } from '@js-joda/core'
import { useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { usePageVisibility } from '../use-page-visibility'
import { isProductionEnv } from '../../utils/api/servers'
import { type RequestedResources } from '../../models/auth/requested-resources'
import { PAGE_PERMISSIONS } from './permissions'
import { setStoredAccessToken } from '../../utils/auth/access-token-store'

export interface AuthAppState {
  isLoading: boolean
  isAuthenticated: boolean
  isAuthorized: boolean
  accessToken: string | null
  requestedResources: RequestedResources
}

export function useAuthorization (): AuthAppState {
  const expiryRef = useRef<number>(-1)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isHidden = usePageVisibility()
  const location = useLocation()
  const [requestedResources, setRequestedResources] = useState<RequestedResources>({ read: [], write: [] })
  useEffect(() => {
    setRequestedResources(PAGE_PERMISSIONS[location.pathname] ?? { read: [], write: [] })
  }, [location])

  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false)
  const [accessToken, setAccessToken] = useState<string | null>(null)

  const getToken = useCallback(async (): Promise<string | Error> => {
    if (!isAuthenticated) {
      setIsLoading(false) // Stop here and let AuthorizationGuard do its authentication
      return '[SKIPPED] User is not authenticated'
    }

    // Past this point, a user can be authenticated (i.e. successfully logged in) but not have
    // a token (e.g. first login) or the previous access token is invalid (e.g. token has expired)

    if (!isHidden && accessToken != null) {
      // Checks if a new access token is needed when page visibility changes
      // E.g. an inactive tab becomes active, switching tabs within the same browser window
      // Note: Tab focus is NOT covered by page visibility
      const claims = jose.decodeJwt(accessToken)
      if (claims.exp != null) {
        const now = LocalDateTime.now()
        const expiryTime = claims.exp
        const rightBeforeExpiry = LocalDateTime
          .ofInstant(Instant.ofEpochSecond(expiryTime))
          .minusSeconds(20)
        if (now.isBefore(rightBeforeExpiry)) {
          return '[SKIPPED] Token is not near its expiry time'
        }
      }
      // Invalid token - continue and fetch a new access token anyway
    }

    let matchingPermissionsCount = 0
    const requestedPermissions = new Set<string>()
    Object.entries(requestedResources)
      .forEach(([accessRight, resources]) => {
        for (const resource of resources) {
          requestedPermissions.add(`${accessRight}:${resource as string}`)
        }
      })

    // getAccessTokenSilently() sometimes throws QuotaExceededError
    // Reference: https://github.com/auth0/auth0-spa-js/issues/455
    let newToken
    try {
      newToken = await getAccessTokenSilently()
    } catch (error) {
      throw new Error('[ERROR] Failed to get access token silently')
    }
    const claims = jose.decodeJwt(newToken)

    if (claims.permissions != null) {
      const permissionsArray = Array.from(new Set<string>(claims.permissions as string[]))
      for (const permission of permissionsArray) {
        if (requestedPermissions.has(permission)) {
          matchingPermissionsCount++
        }
      }

      const isStagingEnv = process.env.REACT_APP_ENVIRONMENT === 'staging'
      const sufficientPermissions = isStagingEnv || requestedPermissions.size === matchingPermissionsCount
      setAccessToken(sufficientPermissions ? newToken : null)
      setStoredAccessToken(sufficientPermissions ? newToken : null) // mirror for basicFetcher / WebSocket
      setIsAuthorized(sufficientPermissions)
      setIsLoading(false)
      return '[DONE] Retrieved access token'
    } else {
      setIsLoading(false)
      return '[UNAUTHORIZED] Invalid token claims'
    }
  }, [isAuthenticated, isHidden, accessToken, location, getAccessTokenSilently, user])

  useEffect(() => {
    getToken()
      .then(value => {
        if (isProductionEnv() ?? false) {
          console.debug(value)
        }
      }, (error) => {
        console.error(error)
        const message: string = error.message
        if (message.includes('browser-tabs-lock')) {
          // getAccessTokenSilently() sometimes throws QuotaExceededError
          // Reference: https://github.com/auth0/auth0-spa-js/issues/455
          window.location.reload()
        }
      })
      .catch(reason => {
        console.error(reason)
      })
  }, [getToken])

  const scheduleAccessTokenRefresh = useCallback((expiryTime: number | undefined): void => {
    // Schedule access token refresh before its expiry
    if (expiryTime != null && (expiryTime - expiryRef.current > 10)) {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      expiryRef.current = expiryTime // Time unit = seconds

      const now = LocalDateTime.now()
      const rightBeforeExpiry = LocalDateTime
        .ofInstant(Instant.ofEpochSecond(expiryTime))
        .minusSeconds(20)
      const diff = now.until(rightBeforeExpiry, ChronoUnit.MILLIS)

      console.debug(`[Auth] Scheduling next access token refresh: ${rightBeforeExpiry.toString()} | Now: ${now.toString()} | Diff: ${diff}ms`)
      // setTimeout from the worker-timers library works even when page visibility is false, or the tab is out of focus
      timeoutRef.current = setTimeout(() => {
        console.debug(`[Auth] Running scheduled access token refresh: ${rightBeforeExpiry.toString()}`)
        void getToken()
      }, diff)
    }
  }, [getToken])

  useEffect(() => {
    if (accessToken == null) {
      return
    }

    const claims = jose.decodeJwt(accessToken)
    scheduleAccessTokenRefresh(claims.exp)

    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [accessToken, scheduleAccessTokenRefresh])

  return { isLoading, isAuthenticated, isAuthorized, accessToken, requestedResources }
}
